import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_MAP_CONT_STATE } from "./constants";
import { IMapContentState } from "./interfaces";
import { IStoreItem, IWasmStoreGeometry, IWasmStoreMarker, TBulkOperations } from "~/wasm/store/interfaces";

const mapSlice = createSlice({
	name: "mapContent",
	initialState: DEFAULT_MAP_CONT_STATE,
	reducers: {
		addMarker: (state: IMapContentState, action: PayloadAction<IWasmStoreMarker>) => {
			state.changeId++;
			state.affectedType = "marker";
			state.affectedGeometryIds = [];
			state.affectedMarkerIds = [action.payload.id];
			state.markers.push(action.payload);
		},
		removeMarker: (state: IMapContentState, action: PayloadAction<IStoreItem["id"]>) => {
			state.changeId++;
			state.affectedType = "marker";
			state.affectedGeometryIds = [];
			state.affectedMarkerIds = [action.payload];
			state.markers = state.markers.filter(item => item.id !== action.payload);
		},
		updateMarker: (state: IMapContentState, action: PayloadAction<{ id: IStoreItem["id"]; data: Partial<IWasmStoreMarker>; }>) => {
			state.changeId++;
			state.affectedType = "marker";
			state.affectedGeometryIds = [];
			state.affectedMarkerIds = [action.payload.id];

			const markerData = state.markers.filter(item => item.id === action.payload.id)[0];

			if (markerData) {
				Object.assign(markerData, action.payload.data);
			}
		},
		addGeometry: (state: IMapContentState, action: PayloadAction<IWasmStoreGeometry>) => {
			state.changeId++;
			state.affectedType = "geometry";
			state.affectedMarkerIds = [];
			state.affectedGeometryIds = [action.payload.id];
			state.geometries.push(action.payload);
		},
		removeGeometry: (state: IMapContentState, action: PayloadAction<IStoreItem["id"]>) => {
			state.changeId++;
			state.affectedType = "geometry";
			state.affectedMarkerIds = [];
			state.affectedGeometryIds = [action.payload];
			state.geometries = state.geometries.filter(item => item.id !== action.payload);
		},
		updateGeometry: (state: IMapContentState, action: PayloadAction<{ id: IStoreItem["id"]; data: Partial<IWasmStoreGeometry>; }>) => {
			state.changeId++;
			state.affectedType = "geometry";
			state.affectedMarkerIds = [];
			state.affectedGeometryIds = [action.payload.id];

			const geometryData = state.geometries.filter(item => item.id === action.payload.id)[0];

			if (geometryData) {
				Object.assign(geometryData, action.payload.data);
			}
		},
		addBulkItems: (state: IMapContentState, action: PayloadAction<Array<TBulkOperations>>) => {
			//console.log(["add bulk items", state, action.payload.slice()]);
			// spocitame
			const markerIds: IMapContentState["affectedMarkerIds"] = [];
			const geometryIds: IMapContentState["affectedGeometryIds"] = [];
			const allOperations = action.payload;
			const deleteMarkerIds: {[key: IStoreItem["id"]]: number} = {};
			const deleteGeometryIds: {[key: IStoreItem["id"]]: number} = {};
			const changeMarkerOpers: {[key: IStoreItem["id"]]: Partial<IWasmStoreMarker>} = [];
			const changeGeometryOpers: {[key: IStoreItem["id"]]: Partial<IWasmStoreGeometry>} = [];

			// nejdrive vsechno pridavani + ulozime id pro mazani
			for (const item of allOperations) {
				switch (item.type) {
					case "add-marker":
						markerIds.push(item.data.id);
						state.markers.push(item.data);
						break;

					case "add-geometry":
						geometryIds.push(item.data.id);
						state.geometries.push(item.data);
						break;

					case "remove-marker":
						markerIds.push(item.id);
						deleteMarkerIds[item.id] = 1;
						break;

					case "remove-geometry":
						geometryIds.push(item.id);
						deleteGeometryIds[item.id] = 1;
						break;

					case "update-marker":
						markerIds.push(item.id);
						changeMarkerOpers[item.id] = item.data;
						break;

					case "update-geometry":
						geometryIds.push(item.id);
						changeGeometryOpers[item.id] = item.data;
						break;

					default:
				}
			}

			let newMarkers: Array<IWasmStoreMarker> = [];
			let newGeometries: Array<IWasmStoreGeometry> = [];

			// mame pridano, roztridene zmeny, pridane id pro smazani
			if (Object.keys(deleteMarkerIds).length > 0 || Object.keys(changeMarkerOpers).length > 0) {
				for (let ind = 0, max = state.markers.length; ind < max; ind++) {
					const marker = state.markers[ind];

					// mazani
					if (marker.id in deleteMarkerIds) {
						delete deleteMarkerIds[marker.id];
						continue;
					}

					// update
					if (marker.id in changeMarkerOpers) {
						Object.assign(marker, changeMarkerOpers[marker.id]);
					}

					newMarkers.push(marker);
				}
			} else {
				newMarkers = state.markers;
			}

			if (Object.keys(deleteGeometryIds).length > 0 || Object.keys(changeGeometryOpers).length > 0) {
				for (let ind = 0, max = state.geometries.length; ind < max; ind++) {
					const geometry = state.geometries[ind];

					// mazani
					if (geometry.id in deleteGeometryIds) {
						delete deleteGeometryIds[geometry.id];
						continue;
					}

					// update
					if (geometry.id in changeGeometryOpers) {
						Object.assign(geometry, changeGeometryOpers[geometry.id]);
					}

					newGeometries.push(geometry);
				}
			} else {
				newGeometries = state.geometries;
			}

			state.markers = newMarkers;
			state.geometries = newGeometries;

			const hasMarkers = markerIds.length > 0;
			const hasGeometries = geometryIds.length > 0;

			state.changeId++;
			state.affectedType = hasMarkers && hasGeometries
				? "both"
				: hasMarkers
					? "marker"
					: "geometry";
			// unikatni
			state.affectedMarkerIds = [...new Set(markerIds)];
			state.affectedGeometryIds = [...new Set(geometryIds)];
			//console.log(["here", state.affectedType, state.affectedMarkerIds, state.affectedGeometryIds, state.markers, state.geometries]);
		},
	},
});

// dle "duck" metodiky jsou akce pojmenovane exporty a reducer default export
export const { addMarker, removeMarker, updateMarker, addGeometry, removeGeometry, updateGeometry, addBulkItems } = mapSlice.actions;
export default mapSlice.reducer;
